import { useEffect, useRef, useState } from 'react';
import { Alert, Collapse, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconClock } from '@tabler/icons-react';
import { useRidesMeService } from '@actschurch/ccms-sdk';
import { currentUpload, docsUsable, isLiveUpload, levelFor, splitMissing, typeNames, typesFor, useMyDocuments } from '@/lib/documents';
import { documentRules, useCopy } from '@/lib/settings';
import { DocumentItem } from '@/components/rides/DocumentItem';
import { useDocumentViewer } from '@/components/rides/DocumentViewer';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { Callout } from '@/components/ui/Callout';
import { LoadingState } from '@/components/ui/LoadingState';
import '@/styles/flows.css';

interface Props {
  /** The API just answered 409 documents_required: emphasise what is missing. */
  highlight?: boolean;
  onChanged?: () => void;
}

/**
 * The driver's documents, short: required ones first (each with its status and Upload button),
 * optional ones folded away under "More documents (optional)", and the privacy small print
 * behind "How we keep your documents safe". Renders nothing when uploads are off.
 */
export function DriverDocuments({ highlight, onChanged }: Props) {
  const { t, settings } = useCopy();
  const rules = documentRules(settings);
  const docs = useMyDocuments();
  const me = useRidesMeService();
  const viewer = useDocumentViewer((uploadId) => me.getDocumentLink(uploadId));
  const [showOptional, setShowOptional] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlight) rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [highlight]);

  if (!rules.enabled) return null;
  if (docs.isLoading) return <LoadingState label="Loading your documents" variant="cards" count={2} />;
  if (docs.error) return <ErrorAlert error={docs.error} title="We couldn't load your documents" onRetry={() => docs.refetch()} />;
  const view = docs.data;
  if (!docsUsable(view)) return null;

  const opts = { clearanceForDrivers: rules.minorRidesRequireClearedDriver };
  const types = typesFor(view, 'driver', opts);
  if (!types.length) return null;

  const required = types.filter((tp) => levelFor(tp, 'driver', opts) === 'required');
  const optional = types.filter((tp) => levelFor(tp, 'driver', opts) !== 'required');
  const missing = splitMissing(view, view.missingForDriver ?? []);
  const approvalRequired = view.approvalRequired ?? rules.approvalRequired;
  const maxMb = view.maxMb && view.maxMb > 0 ? Math.min(20, view.maxMb) : rules.maxMb;
  const retentionDays = view.retentionDays ?? rules.retentionDays;
  const optionalAdded = optional.filter((tp) => isLiveUpload(currentUpload(view, tp.uploadTypeID))).length;

  const item = (tp: (typeof types)[number], level: 'required' | 'optional') => (
    <DocumentItem
      key={tp.uploadTypeID}
      type={tp}
      upload={currentUpload(view, tp.uploadTypeID)}
      level={level}
      requiredText="Needed before riders can book your lift"
      approvalRequired={approvalRequired}
      maxMb={maxMb}
      highlight={highlight && missing.toUpload.some((m) => m.uploadTypeID === tp.uploadTypeID)}
      onView={(u) => viewer.open(u.rideUploadID, tp.name, u.contentType)}
      viewing={viewer.loadingId !== null && viewer.loadingId === currentUpload(view, tp.uploadTypeID)?.rideUploadID}
      onChanged={onChanged}
    />
  );

  return (
    <div ref={rootRef} style={{ scrollMarginTop: 80 }}>
      <Stack gap="md">
        {missing.toUpload.length > 0 && (
          <Callout tone={highlight ? 'bad' : 'warn'} live={highlight}>
            {t('docs.required', undefined, { documents: typeNames(missing.toUpload) })}
          </Callout>
        )}
        {missing.toUpload.length === 0 && missing.awaitingReview.length > 0 && (
          <Callout tone="info" icon={<IconClock size={20} />}>{t('docs.approval_pending')}</Callout>
        )}

        {required.length > 0 && <div className="rides-stack">{required.map((tp) => item(tp, 'required'))}</div>}

        {optional.length > 0 && (
          <div>
            <button type="button" className="rides-disclose" aria-expanded={showOptional} onClick={() => setShowOptional((v) => !v)}>
              <span>
                More documents (optional)
                <small>{optionalAdded ? `${optionalAdded} of ${optional.length} added` : t('drive.docs_optional')}</small>
              </span>
              <IconChevronDown size={22} aria-hidden="true" />
            </button>
            <Collapse in={showOptional}>
              <div className="rides-stack" style={{ marginTop: 12 }}>{optional.map((tp) => item(tp, 'optional'))}</div>
            </Collapse>
          </div>
        )}

        {viewer.error && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} radius="md" role="alert" withCloseButton onClose={viewer.clearError}>
            {viewer.error}
          </Alert>
        )}

        <div>
          <button type="button" className="rides-linkbtn" aria-expanded={showPrivacy} onClick={() => setShowPrivacy((v) => !v)}>
            How we keep your documents safe
          </button>
          <Collapse in={showPrivacy}>
            <Text size="sm" c="dimmed" mt={4}>
              {t('docs.privacy_note', undefined, { retentionDays })} Photos are made smaller and their location data is removed on your phone before upload.
            </Text>
          </Collapse>
        </div>
      </Stack>
      {viewer.modal}
    </div>
  );
}
